import { LLM } from '@/types'

import { CATEGORIES } from '../categories'

// AWS Bedrock chat models
// (ADDED 07/09/26, Nova Pro added 07/10/26) -----------------------------
//
// Grok/GLM/DeepSeek/Kimi are the Mantle catalog served through Bedrock's
// OpenAI-Chat-Completions-compatible endpoint — see
// lib/bedrock/mantle-client.ts. Nova Pro is Amazon's own model; it is NOT
// available through that endpoint and instead goes through the native
// Converse API — see lib/bedrock/nova-converse-client.ts.

const BEDROCK_PLATFORM_LINK = 'https://docs.aws.amazon.com/bedrock/latest/userguide/model-cards.html'

const GROK_4_3: LLM = {
  modelId: 'xai.grok-4.3',
  modelName: 'Grok 4.3',
  provider: 'bedrock',
  hostedId: 'xai.grok-4.3',
  platformLink: 'https://docs.aws.amazon.com/bedrock/latest/userguide/model-card-xai-grok-4-3.html',
  imageInput: true,
  tier: 'pro',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () => 'xAI Grok 4.3 via AWS Bedrock Mantle — 1M context, reasoning-first, multimodal input',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 1.25,
    outputCost: 2.5
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.MULTIMODAL,
    CATEGORIES.REASONING
  ]
}

const GLM_4_7: LLM = {
  modelId: 'zai.glm-4.7',
  modelName: 'GLM-4.7',
  provider: 'bedrock',
  hostedId: 'zai.glm-4.7',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () => 'Zhipu AI’s GLM-4.7 via AWS Bedrock — 200K context, strong at coding and agentic tasks',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.6,
    outputCost: 2.2
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.PROGRAMMING_SCRIPTING
  ]
}

const GLM_4_7_FLASH: LLM = {
  modelId: 'zai.glm-4.7-flash',
  modelName: 'GLM-4.7 Flash',
  provider: 'bedrock',
  hostedId: 'zai.glm-4.7-flash',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'free',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () => 'Lightweight, low-cost variant of GLM-4.7 via AWS Bedrock — 200K context',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.15,
    outputCost: 0.5
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING]
}

const DEEPSEEK_V3_2: LLM = {
  modelId: 'deepseek.v3.2',
  modelName: 'DeepSeek V3.2',
  provider: 'bedrock',
  hostedId: 'deepseek.v3.2',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () => 'DeepSeek’s V3.2 via AWS Bedrock — ~256K context, strong reasoning and coding',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.4,
    outputCost: 1.2
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.SCIENCE,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.REASONING
  ]
}

const KIMI_K2_5: LLM = {
  modelId: 'moonshotai.kimi-k2.5',
  modelName: 'Kimi K2.5',
  provider: 'bedrock',
  hostedId: 'moonshotai.kimi-k2.5',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: true,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () => 'Moonshot AI’s Kimi K2.5 via AWS Bedrock — 256K context, multimodal',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.5,
    outputCost: 1.8
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.MULTIMODAL,
    CATEGORIES.REASONING
  ]
}

// Bedrock requires a CRIS inference profile ID (global./us.) — bare
// `moonshotai.kimi-k3` returns 400 "on-demand throughput isn’t supported".
const KIMI_K3: LLM = {
  modelId: 'global.moonshotai.kimi-k3',
  modelName: 'Kimi K3',
  provider: 'bedrock',
  hostedId: 'global.moonshotai.kimi-k3',
  platformLink:
    'https://docs.aws.amazon.com/bedrock/latest/userguide/model-card-moonshot-ai-kimi-k3.html',
  imageInput: true,
  tier: 'pro',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'Moonshot AI’s Kimi K3 via AWS Bedrock (global CRIS) — 2.8T MoE, 1M context, native vision, tool calling, explicit prompt caching',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    // Global CRIS Standard (Sep 2026 AWS pricing card)
    inputCost: 3.0,
    outputCost: 15.0
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.MULTIMODAL,
    CATEGORIES.REASONING
  ]
}

const NOVA_PRO: LLM = {
  modelId: 'amazon.nova-pro-v1:0',
  modelName: 'Nova Pro',
  provider: 'bedrock',
  hostedId: 'amazon.nova-pro-v1:0',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: true,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'Amazon Nova Pro via AWS Bedrock — 300K context, multimodal (text, image, video). Also used as the large-context fallback for the other Bedrock models above.',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.8,
    outputCost: 3.2
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.MULTIMODAL, CATEGORIES.REASONING]
}

// Live-probed 2026-09-30: text + tools + image_url on Chat Completions.
const QWEN3_VL_235B: LLM = {
  modelId: 'qwen.qwen3-vl-235b-a22b',
  modelName: 'Qwen3 VL 235B',
  provider: 'bedrock',
  hostedId: 'qwen.qwen3-vl-235b-a22b',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: true,
  tier: 'pro',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'Qwen3 VL 235B via AWS Bedrock — multimodal vision + tool calling (live-verified)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.7,
    outputCost: 2.8
  },
  categories: [
    CATEGORIES.TECHNOLOGY,
    CATEGORIES.PROGRAMMING,
    CATEGORIES.MULTIMODAL,
    CATEGORIES.REASONING
  ]
}

const QWEN3_CODER_NEXT: LLM = {
  modelId: 'qwen.qwen3-coder-next',
  modelName: 'Qwen3 Coder Next',
  provider: 'bedrock',
  hostedId: 'qwen.qwen3-coder-next',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'Qwen3 Coder Next via AWS Bedrock — coding-focused with tool calling (text-only)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.4,
    outputCost: 1.6
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING]
}

const QWEN3_NEXT_80B: LLM = {
  modelId: 'qwen.qwen3-next-80b-a3b',
  modelName: 'Qwen3 Next 80B',
  provider: 'bedrock',
  hostedId: 'qwen.qwen3-next-80b-a3b',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'Qwen3 Next 80B via AWS Bedrock — fast general model with tool calling (text-only)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.35,
    outputCost: 1.4
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING, CATEGORIES.REASONING]
}

const MINIMAX_M2_5: LLM = {
  modelId: 'minimax.minimax-m2.5',
  modelName: 'MiniMax M2.5',
  provider: 'bedrock',
  hostedId: 'minimax.minimax-m2.5',
  platformLink:
    'https://docs.aws.amazon.com/bedrock/latest/userguide/model-card-minimax-minimax-m2.html',
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'MiniMax M2.5 via AWS Bedrock — agent/tool-native (text-only; no image modality)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.3,
    outputCost: 1.2
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING, CATEGORIES.REASONING]
}

const MINIMAX_M2_1: LLM = {
  modelId: 'minimax.minimax-m2.1',
  modelName: 'MiniMax M2.1',
  provider: 'bedrock',
  hostedId: 'minimax.minimax-m2.1',
  platformLink:
    'https://docs.aws.amazon.com/bedrock/latest/userguide/model-card-minimax-minimax-m2.html',
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'MiniMax M2.1 via AWS Bedrock — strong tools/coding (text-only; no image modality)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.3,
    outputCost: 1.2
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING]
}

const GPT_OSS_120B: LLM = {
  modelId: 'openai.gpt-oss-120b-1:0',
  modelName: 'GPT-OSS 120B',
  provider: 'bedrock',
  hostedId: 'openai.gpt-oss-120b-1:0',
  platformLink: BEDROCK_PLATFORM_LINK,
  imageInput: false,
  tier: 'plus',
  tools: true,
  new: true,
  supportsStreaming: true,
  description: () =>
    'OpenAI GPT-OSS 120B via AWS Bedrock — open-weight with tool calling (text-only)',
  pricing: {
    currency: 'USD',
    unit: '1M tokens',
    inputCost: 0.15,
    outputCost: 0.6
  },
  categories: [CATEGORIES.TECHNOLOGY, CATEGORIES.PROGRAMMING, CATEGORIES.REASONING]
}

export const BEDROCK_LLM_LIST: LLM[] = [
  GROK_4_3,
  KIMI_K3,
  QWEN3_VL_235B,
  MINIMAX_M2_5,
  MINIMAX_M2_1,
  QWEN3_CODER_NEXT,
  QWEN3_NEXT_80B,
  GPT_OSS_120B,
  GLM_4_7,
  GLM_4_7_FLASH,
  DEEPSEEK_V3_2,
  KIMI_K2_5,
  NOVA_PRO
]
